import type { Section } from "./types";

// Field weights
const W_TITLE = 10;
const W_TITLES = 3;
const W_CONTENT = 1;

// Match quality multipliers
const M_EXACT = 3; // token === word
const M_PREFIX = 2; // word starts with token
const M_FUZZY = 1; // within edit distance threshold

// Phrase bonus (query appearing in title/content as a contiguous substring)
const PHRASE_BONUS = 15;

// Awarded when the section's title equals the query exactly (case-insensitive,
// post-normalize). Sized to outweigh any combination of search_boost, level,
// position, and active-site bonuses so an exact-title match always wins.
const TITLE_EXACT_BONUS = 80;

// Awarded when the title starts with the query (e.g. query "invoice" against
// "Invoice details"). Smaller than the exact bonus but enough to rank above
// titles where the query appears mid-string or only in content.
const TITLE_PREFIX_BONUS = 30;

const MAX_RESULTS = 8;

export function levenshtein(a: string, b: string): number {
  const n = b.length;
  const row: number[] = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let corner = row[0] as number;
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const upper = row[j] as number;
      row[j] =
        a[i - 1] === b[j - 1]
          ? corner
          : 1 + Math.min(corner, row[j - 1] as number, upper);
      corner = upper;
    }
  }
  return row[n] as number;
}

export function fuzzyThreshold(len: number): number {
  if (len <= 3) return 0;
  if (len <= 6) return 1;
  return 2;
}

function splitWords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[\s\-_.,;:!?()[\]{}"'/\\]+/)
    .filter(Boolean);
}

// Pre-tokenized view of a Section. The string work (splitWords, toLowerCase)
// is identical for every keystroke, so we do it once at index time instead of
// re-deriving it inside the per-keystroke scoring loop.
export interface IndexedSection {
  section: Section;
  titleWords: string[];
  titlesWords: string[];
  contentLower: string;
  titleLower: string;
  // 0..1 authority from array order, computed *within* the section's own zone
  // (1 = first/most authoritative). Per-zone so commercial entries aren't
  // boosted just for being concatenated ahead of docs in the merged array.
  authority: number;
}

function indexFields(section: Section) {
  return {
    section,
    titleWords: splitWords(section.title ?? ""),
    titlesWords: (section.titles ?? []).flatMap((t) => splitWords(t)),
    contentLower: (section.content ?? "").toLowerCase(),
    titleLower: (section.title ?? "").toLowerCase(),
  };
}

export function buildSearchIndex(sections: Section[]): IndexedSection[] {
  const zoneTotals = new Map<string, number>();
  for (const s of sections) {
    const z = s.zone ?? "";
    zoneTotals.set(z, (zoneTotals.get(z) ?? 0) + 1);
  }
  const zoneSeen = new Map<string, number>();
  return sections.map((section) => {
    const z = section.zone ?? "";
    const pos = zoneSeen.get(z) ?? 0;
    zoneSeen.set(z, pos + 1);
    const total = zoneTotals.get(z) ?? 1;
    return {
      ...indexFields(section),
      authority: total > 1 ? 1 - pos / total : 1,
    };
  });
}

// Score a query token against a word list (title/breadcrumb fields).
// Returns a match multiplier: M_EXACT | M_PREFIX | M_FUZZY | 0 (no match).
function wordListMatch(token: string, words: string[]): number {
  let best = 0;
  const threshold = fuzzyThreshold(token.length);
  for (const word of words) {
    if (word === token) return M_EXACT;
    if (word.startsWith(token)) {
      best = Math.max(best, M_PREFIX);
      continue;
    }
    if (threshold > 0) {
      const dist = levenshtein(token, word);
      if (dist <= threshold) best = Math.max(best, M_FUZZY);
    }
  }
  return best;
}

// Returns null when any token has no match (section excluded from results).
export function scoreSection(idx: IndexedSection, tokens: string[]): number | null {
  const { section, titleWords, titlesWords, contentLower } = idx;

  let total = 0;
  for (const token of tokens) {
    const titleMatch = wordListMatch(token, titleWords);
    const titlesMatch = wordListMatch(token, titlesWords);
    const contentMatch = contentLower.includes(token) ? 1 : 0;

    if (!titleMatch && !titlesMatch && !contentMatch) return null;

    total +=
      titleMatch * W_TITLE + titlesMatch * W_TITLES + contentMatch * W_CONTENT;
  }

  // Higher section levels (h1 = level 1) are more authoritative
  total += Math.max(0, 4 - section.level);

  // Author-defined boost — all sections of the page inherit this value via extraFields
  if (section.search_boost) total += section.search_boost;

  return total;
}

// Max bonus awarded to the first section in the array (index 0).
// Decays linearly to 0 for the last section.
// Kept small so it only breaks ties — content relevance still dominates.
const POSITION_BONUS_MAX = 4;

// Bonus for sections within the currently active site/sub-site.
const ACTIVE_SITE_BONUS = 6;

// Bonus for sections in the zone the user is currently browsing (commercial
// vs docs). Sized to win ties and lift the current zone above equal matches in
// the other zone, but kept below TITLE_PREFIX_BONUS (30) so a direct title hit
// in the *other* zone still surfaces — both zones remain findable everywhere.
const ZONE_BONUS = 12;

// Minimum slots reserved for the zone the user is NOT currently in, provided
// that zone has matches. Guarantees cross-zone discoverability: searching from
// a commercial page always surfaces relevant docs (and vice-versa) even when
// the current zone would otherwise fill every slot.
const CROSS_ZONE_FLOOR = 2;

type Scored = { section: Section; score: number };

// Take the top MAX_RESULTS by score, but if the other zone has matches and is
// under-represented, swap the lowest-scoring current-zone entries for the
// highest-scoring other-zone ones until the floor is met. `scored` is sorted
// score-descending.
function pickWithCrossZoneFloor(
  scored: Scored[],
  currentZone?: import("./types").SearchZone,
): Section[] {
  const top = scored.slice(0, MAX_RESULTS);
  if (!currentZone) return top.map((r) => r.section);

  const other = currentZone === "docs" ? "commercial" : "docs";
  const haveOther = top.filter((r) => r.section.zone === other).length;
  if (haveOther >= CROSS_ZONE_FLOOR) return top.map((r) => r.section);

  const extraOther = scored
    .slice(MAX_RESULTS)
    .filter((r) => r.section.zone === other)
    .slice(0, CROSS_ZONE_FLOOR - haveOther);
  if (!extraOther.length) return top.map((r) => r.section);

  // Drop the lowest-scoring current-zone entries (iterate from the tail) to
  // make room, preserving any other-zone entries already in the top set.
  let removable = extraOther.length;
  const kept: Scored[] = [];
  for (let i = top.length - 1; i >= 0; i--) {
    const r = top[i]!;
    if (removable > 0 && r.section.zone !== other) {
      removable--;
      continue;
    }
    kept.push(r);
  }
  kept.reverse();
  return [...kept, ...extraOther]
    .sort((a, b) => b.score - a.score)
    .map((r) => r.section);
}

// `indexed` is the prebuilt, locale-scoped index (see buildSearchIndex). It is
// constructed once per load/locale and reused across every keystroke, so the
// per-keystroke cost here is just token comparison — no re-tokenizing.
export function filterAndRank(
  indexed: IndexedSection[],
  query: string,
  localePrefix: string,
  activeSite?: string,
  currentZone?: import("./types").SearchZone,
): Section[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];

  const phrase = tokens.join(" ");
  const activeSitePrefix = activeSite ? localePrefix + activeSite + "/" : null;

  const scored: Scored[] = [];

  for (const idx of indexed) {
    const base = scoreSection(idx, tokens);
    if (base === null) continue;

    let score = base;
    const { section: s, titleLower, contentLower } = idx;

    // Title-shape bonuses — these escalate so that an exact-title match always
    // beats a title-prefix match, which always beats a title-contains match.
    if (titleLower === phrase) {
      score += TITLE_EXACT_BONUS;
    } else if (titleLower.startsWith(phrase)) {
      score += TITLE_PREFIX_BONUS;
    } else if (titleLower.includes(phrase)) {
      score += PHRASE_BONUS;
    } else if (contentLower.includes(phrase)) {
      score += Math.floor(PHRASE_BONUS / 2);
    }

    // Within-zone authority ([N] section ordering), precomputed at index time.
    score += Math.round(idx.authority * POSITION_BONUS_MAX);

    // Sections within the current site rank above equal matches from other sites
    if (activeSitePrefix && s.id.startsWith(activeSitePrefix))
      score += ACTIVE_SITE_BONUS;

    // Sections in the zone the user is currently in rank above equal matches
    // from the other zone (commercial ↔ docs).
    if (currentZone && s.zone === currentZone) score += ZONE_BONUS;

    scored.push({ section: s, score });
  }

  scored.sort((a, b) => b.score - a.score);

  // Dedup by id. A result row is an `<e-search>` option keyed by `value`
  // (= section.id) and highlight is keyed by that value, so two rows sharing
  // an id (e.g. colliding heading anchors) collapse to one keyboard-nav entry
  // and the arrow cursor skips a row. Keep the highest-scoring instance.
  const seen = new Set<string>();
  const unique = scored.filter(
    (r) => !seen.has(r.section.id) && seen.add(r.section.id),
  );

  return pickWithCrossZoneFloor(unique, currentZone);
}
